import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Image,
  Inscription,
  NavigationLeftIcon,
  RawButton,
  Responsive,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import styled from 'styled-components';

// import { VisualImpairment } from '@/modules/core/components';
import LogoIcon from '@/assets/logos/login-logo.svg';
import MDCityIcon from '@/assets/placeholders/city-hd.svg';
import SMCityIcon from '@/assets/placeholders/city-laptop.svg';
import LGCityIcon from '@/assets/placeholders/city-uhd.svg';
// import CloudsIcon from '@/assets/placeholders/clouds.svg';
import theme, { LG_QUERY, MD_QUERY, SM_QUERY } from '@/styles/theme';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import { BackButtonWrapper, BackgroundWrapper, FormWrapper } from './SignInWrapper.styles';

const isRmrTheme = whiteLabelingConfig.key === THEMES.rmr;
const isABSTheme = whiteLabelingConfig.key === THEMES.abs;
const logoWidth = isRmrTheme ? 330 : 240;
const logoHeight = isRmrTheme ? 159 : 65;

interface Props {
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  isBackButton?: boolean;
  onBack?: () => void;
  flat?: boolean;
  header?: React.ReactNode;
  isTitleLess?: boolean;
}

interface ImageProps {
  size?: typeof SM_QUERY | typeof MD_QUERY | typeof LG_QUERY;
}

const ImageWrapper = styled(Image)<ImageProps>`
  display: none;
  ${({ size }) => size} {
    display: block;

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const SignInWrapper: React.FC<Props> = ({
  flat,
  children,
  title,
  subtitle,
  isBackButton: backButton,
  onBack,
  header,
  isTitleLess,
}) => {
  const { isMobile } = useResponsive();
  const BackgroundNode = useMemo(() => {
    if (isRmrTheme) {
      return (
        <BackgroundWrapper>
          <>
            <ImageWrapper
              src={whiteLabelingConfig?.images?.smLoginBackground || SMCityIcon}
              alt=""
              size={SM_QUERY}
              width="100%"
            />
            <ImageWrapper
              src={whiteLabelingConfig?.images?.mdLoginBackground || MDCityIcon}
              alt=""
              size={MD_QUERY}
              width="100%"
            />
            <ImageWrapper
              src={whiteLabelingConfig?.images?.lgLoginBackground || LGCityIcon}
              alt=""
              size={LG_QUERY}
              width="100%"
            />
          </>
        </BackgroundWrapper>
      );
    }
    return <BackgroundWrapper imageSrc={whiteLabelingConfig?.images?.lgLoginBackground || LGCityIcon} />;
  }, []);

  //   height: 100vh;
  // width: 100%;
  // display: flex;
  // flex-direction: column;
  // justify-content: center;
  // position: absolute;
  // overflow: hidden;
  // background-color: ${({ theme }) => theme.colors.module};
  return (
    <Box
      height="100vh"
      width="100%"
      direction="column"
      background="module"
      justify={isMobile ? 'start' : 'center'}
      style={{ position: 'absolute' }}
      overflow="hidden"
    >
      {BackgroundNode}
      <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
        <Box flex height={{ min: '100px', max: '100px' }} />
      </Responsive>
      <Box
        direction={isMobile ? 'column' : 'row'}
        style={{
          zIndex: 1,
        }}
        pad={{ horizontal: isMobile ? '10px' : '100px' }}
      >
        <Box
          direction="column"
          flex
          align={isMobile ? 'center' : undefined}
          margin={{
            right: isMobile ? undefined : 'spacing96',
            top: isMobile ? 'spacing48' : '80px',
          }}
          height="large"
        >
          <Image
            src={whiteLabelingConfig?.images?.signInLogo || whiteLabelingConfig?.logo || LogoIcon}
            width={whiteLabelingConfig?.loginIconWidth || logoWidth}
            alt="logo"
            height={isMobile ? '30px' : whiteLabelingConfig?.loginIconHeight || logoHeight}
          />
          {!isTitleLess && (
            <Text
              size={isMobile ? 'small' : 'xxlarge'}
              margin={isMobile ? { top: 'spacing12', horizontal: 'spacing12' } : { top: 'spacing24' }}
              color="loginTitleColor"
              textAlign={isMobile ? 'center' : 'start'}
              weight={isMobile ? 500 : 400}
              style={{
                maxWidth: isRmrTheme ? '400px' : isABSTheme ? '450px' : '600px',
              }}
            >
              {whiteLabelingConfig?.loginScreenTitle ||
                'Radically transforming the consumer benefit experience with an ultra-reliable, automated, and AI-powered solution. '}
            </Text>
          )}
        </Box>
        <Box direction="column" margin={isMobile ? { top: 'spacing32' } : undefined}>
          <FormWrapper flat={flat} isMobileView={isMobile} isBackButton={backButton}>
            {backButton && (
              <BackButtonWrapper isMobileView={isMobile}>
                <RawButton onClick={onBack} data-testid="back-button" type="button">
                  <Box
                    elevation="default"
                    width="40px"
                    height="40px"
                    round="checkboxRound"
                    align="center"
                    justify="center"
                    background="canvas"
                  >
                    <NavigationLeftIcon size="24px" color="iconPrimary" />
                  </Box>
                </RawButton>
              </BackButtonWrapper>
            )}
            {header}
            {title && typeof title === 'string' ? (
              <Heading size={theme.spacings.spacing32} color="textTitle" textAlign="center" level={1}>
                {title}
              </Heading>
            ) : (
              title
            )}
            {subtitle && (
              <Inscription
                size="large"
                lineHeight="22px"
                textAlign={isMobile ? 'start' : 'center'}
                margin={{ top: 'spacing8' }}
              >
                {subtitle}
              </Inscription>
            )}
            <Box direction="column" margin={{ top: isMobile ? 'spacing24' : 'spacing32' }}>
              {children}
            </Box>
          </FormWrapper>
        </Box>
      </Box>
    </Box>
  );
};
export default SignInWrapper;
